import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface DashboardKPIsDTO {
  extratosImportadosUltimoMes: {
    quantidadeExtratos: number;
    quantidadeLinhas: number;
    periodo: string;
  };
  valorTotalEntradas: {
    valorTotal: number;
    quantidadeLancamentos: number;
    mediaPorLancamento: number;
    periodo: string;
  };
  valorTotalSaidas: {
    valorTotal: number;
    quantidadeLancamentos: number;
    mediaPorLancamento: number;
    periodo: string;
  };
  evolucaoFinanceiraMensal: Array<{
    mes: string;
    receitas: number;
    despesas: number;
    saldo: number;
  }>;
  receitasPorCliente: Array<{
    cliente: string;
    valor: number;
    percentual: number;
  }>;
  periodoInicio: string;
  periodoFim: string;
  usuarioId: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private apiUrl = environment.advContabilApi;

  constructor(private http: HttpClient) {}

  getDashboardKPIs(usuarioId: string, startDate?: Date, endDate?: Date): Observable<DashboardKPIsDTO> {
    let params = new HttpParams().set('usuarioId', usuarioId);
    
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }

    const url = `${this.apiUrl}/api/Monitoring/GetDashboardKPIs/dashboard-kpis`;

    return this.http.get<DashboardKPIsDTO>(url, { params }).pipe(
      catchError(error => {
        console.error('❌ Erro ao buscar KPIs:', error);
        throw error;
      }),
      shareReplay(1) 
    );
  }

  getAllDashboardData(usuarioId: string): Observable<{
    kpis: any[];
    chartData: any;
    receitasPorCliente: any;
  }> {
    const hoje = new Date();
    const inicioAno = new Date(hoje.getFullYear(), 0, 1);
    
    return new Observable(observer => {
      this.getDashboardKPIs(usuarioId, inicioAno, hoje).subscribe({
        next: (kpis) => {
          const formattedKpis = [
            { 
              title: 'Extratos Importados (últimos 3 meses)', 
              value: `${kpis.extratosImportadosUltimoMes.quantidadeExtratos} (${kpis.extratosImportadosUltimoMes.quantidadeLinhas} linhas)`, 
              icon: 'pi pi-download', 
              color: '#1976d2' 
            },
            { 
              title: 'Valor Total Entradas Extratos (últimos 3 meses)', 
              value: `R$ ${kpis.valorTotalEntradas.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
              icon: 'pi pi-arrow-up', 
              color: '#43a047' 
            },
            { 
              title: 'Valor Total Saídas Extratos (últimos 3 meses)', 
              value: `R$ ${kpis.valorTotalSaidas.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
              icon: 'pi pi-arrow-down', 
              color: '#e53935' 
            }
          ];

          const chartData = {
            labels: kpis.evolucaoFinanceiraMensal.map(item => item.mes),
            datasets: [
              { 
                label: 'Entradas (Extratos)', 
                data: kpis.evolucaoFinanceiraMensal.map(item => item.receitas), 
                backgroundColor: '#43a047' 
              },
              { 
                label: 'Saídas (Extratos)', 
                data: kpis.evolucaoFinanceiraMensal.map(item => item.despesas), 
                backgroundColor: '#e53935' 
              }
            ]
          };

          const coresDinamicas = [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
            '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
          ];
          
          const receitasPorCliente = {
            labels: kpis.receitasPorCliente.map(item => {
              const nome = item.cliente.length > 20 ? 
                item.cliente.substring(0, 17) + '...' : 
                item.cliente;
              return nome;
            }),
            datasets: [{
              data: kpis.receitasPorCliente.map(item => item.valor),
              backgroundColor: kpis.receitasPorCliente.map((_, index) => 
                coresDinamicas[index % coresDinamicas.length]
              ),
              borderWidth: 2,
              borderColor: '#ffffff'
            }]
          };



          observer.next({
            kpis: formattedKpis,
            chartData: chartData,
            receitasPorCliente: receitasPorCliente
          });
          observer.complete();
        },
        error: (error) => {
          console.error('Erro ao buscar dados do dashboard:', error);
          observer.next({
            kpis: this.getMockKpis(),
            chartData: this.getMockChartData(),
            receitasPorCliente: this.getMockReceitasPorCliente()
          });
          observer.complete();
        }
      });
    });
  }

  getKpis(usuarioId: string): Observable<any[]> {
    const hoje = new Date();
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    
    return new Observable(observer => {
      this.getDashboardKPIs(usuarioId, inicioMes, hoje).subscribe({
        next: (kpis) => {
          const formattedKpis = [
            { 
              title: 'Extratos Importados (últimos 3 meses)', 
              value: `${kpis.extratosImportadosUltimoMes.quantidadeExtratos} (${kpis.extratosImportadosUltimoMes.quantidadeLinhas} linhas)`, 
              icon: 'pi pi-download', 
              color: '#1976d2' 
            },
            { 
              title: 'Valor Total Entradas Extratos (últimos 3 meses)', 
              value: `R$ ${kpis.valorTotalEntradas.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
              icon: 'pi pi-arrow-up', 
              color: '#43a047' 
            },
            { 
              title: 'Valor Total Saídas Extratos (últimos 3 meses)', 
              value: `R$ ${kpis.valorTotalSaidas.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
              icon: 'pi pi-arrow-down', 
              color: '#e53935' 
            }
          ];
          observer.next(formattedKpis);
          observer.complete();
        },
        error: (error) => {
          console.error('Erro ao buscar KPIs:', error);
          observer.next(this.getMockKpis());
          observer.complete();
        }
      });
    });
  }

  getChartData(usuarioId: string): Observable<any> {
    const hoje = new Date();
    const inicioAno = new Date(hoje.getFullYear(), 0, 1);
    
    return new Observable(observer => {
      this.getDashboardKPIs(usuarioId, inicioAno, hoje).subscribe({
        next: (kpis) => {
          const chartData = {
            labels: kpis.evolucaoFinanceiraMensal.map(item => item.mes),
            datasets: [
              { 
                label: 'Receitas (Extratos)', 
                data: kpis.evolucaoFinanceiraMensal.map(item => item.receitas), 
                backgroundColor: '#43a047' 
              },
              { 
                label: 'Despesas (Extratos)', 
                data: kpis.evolucaoFinanceiraMensal.map(item => item.despesas), 
                backgroundColor: '#e53935' 
              }
            ]
          };
          observer.next(chartData);
          observer.complete();
        },
        error: (error) => {
          console.error('Erro ao buscar dados de evolução financeira:', error);
          observer.next(this.getMockChartData());
          observer.complete();
        }
      });
    });
  }

  getReceitasPorCliente(usuarioId: string): Observable<any> {
    const hoje = new Date();
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    
    return new Observable(observer => {
      this.getDashboardKPIs(usuarioId, inicioMes, hoje).subscribe({
        next: (kpis) => {
          const coresDinamicas = [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
            '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
          ];
          
          const chartData = {
            labels: kpis.receitasPorCliente.map(item => {
              const nome = item.cliente.length > 20 ? 
                item.cliente.substring(0, 17) + '...' : 
                item.cliente;
              return nome;
            }),
            datasets: [
              { 
                data: kpis.receitasPorCliente.map(item => item.valor), 
                backgroundColor: kpis.receitasPorCliente.map((_, index) => 
                  coresDinamicas[index % coresDinamicas.length]
                ),
                borderWidth: 2,
                borderColor: '#ffffff'
              }
            ]
          };
          
          observer.next(chartData);
          observer.complete();
        },
        error: (error) => {
          console.error('Erro ao buscar receitas por cliente:', error);
          observer.next(this.getMockReceitasPorCliente());
          observer.complete();
        }
      });
    });
  }



  getPendencias(): Observable<any[]> {
    return of([
      { descricao: 'Importar extrato bancário do mês', acao: 'Importar', link: '/importacao-extrato-bancario' },
      { descricao: 'Emitir NFS-e para cliente X', acao: 'Emitir', link: '/emissao-nfse' },
      { descricao: 'Baixar guia de pagamento de tributo', download: true },
      { descricao: 'Entregar declaração mensal', acao: 'Entregar', link: '/listar-declaracoes-fiscais' }
    ]);
  }

  getQuickActions(): Observable<any[]> {
    return of([
      { 
        label: 'Importar Extrato', 
        icon: 'pi pi-download', 
        link: '/importacao-extrato-bancario',
        legend: 'Importe extratos bancários via OFX ou planilha para classificar transações'
      },
      { 
        label: 'Emitir NFS-e', 
        icon: 'pi pi-file-export', 
        link: '/emissao-nfse',
        legend: 'Emita notas fiscais de serviço eletrônicas para seus clientes'
      },
      { 
        label: 'Baixar Guia', 
        icon: 'pi pi-arrow-down', 
        link: '/tributos/pagar',
        legend: 'Baixe as guias de pagamento de tributos geradas pelo sistema'
      },
      { 
        label: 'Visualizar Declarações Enviadas', 
        icon: 'pi pi-file-edit', 
        link: '/listar-declaracoes-fiscais',
        legend: 'Visualize e baixe declarações fiscais já entregues pelo sistema Advventure, incluindo recibos e protocolos'
      }
    ]);
  }

  getDespesasPorCategoria(): Observable<any> {
    return of({
      labels: ['Impostos', 'Taxas Bancárias', 'Fornecedores', 'Outros'],
      datasets: [
        { data: [0, 0, 0, 0], backgroundColor: ['#e53935', '#fb8c00', '#1976d2', '#bdbdbd'] }
      ]
    });
  }

  getTributosPorStatus(): Observable<any> {
    return of({
      labels: ['Pagas', 'Pendentes', 'Vencidas'],
      datasets: [
        { data: [0, 0, 0], backgroundColor: ['#43a047', '#fb8c00', '#e53935'] }
      ]
    });
  }

  private getMockKpis(): any[] {
    return [
      { title: 'Extratos Importados (últimos 3 meses)', value: 'Sem informações suficientes', icon: 'pi pi-download', color: '#1976d2' },
      { title: 'Valor Total Entradas Extratos (últimos 3 meses)', value: 'Sem informações suficientes', icon: 'pi pi-arrow-up', color: '#43a047' },
      { title: 'Valor Total Saídas Extratos (últimos 3 meses)', value: 'Sem informações suficientes', icon: 'pi pi-arrow-down', color: '#e53935' }
    ];
  }

  private getMockChartData(): any {
    return {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      datasets: [
        { label: 'Receitas (Extratos)', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], backgroundColor: '#43a047' },
        { label: 'Despesas (Extratos)', data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], backgroundColor: '#e53935' }
      ]
    };
  }

  private getMockReceitasPorCliente(): any {
    const coresDinamicas = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    
    return {
      labels: ['Cliente A', 'Cliente B', 'Cliente C', 'Cliente D'],
      datasets: [
        { 
          data: [0, 0, 0, 0], 
          backgroundColor: coresDinamicas.slice(0, 4),
          borderWidth: 2,
          borderColor: '#ffffff'
        }
      ]
    };
  }


} 
